import { array } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { get, identity, map } from 'lodash/fp';
import { createSelector } from 'reselect';
import { PricingModeKey } from '@mews/enums';
import { getClientName } from '../../../../modules/configuration/selectors';
import { localizeProperty } from '../../../../modules/globalization/selectors';
import { getPromotedServiceCategories } from '../../../../modules/promotedServiceCategories';
import { getSelectedEnterpriseCategories } from '../../../../modules/resourceCategories/selectors';
import {
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseDefaultLanguageName,
    getSelectedEnterpriseDefaultRateCurrencyCode,
} from '../../../../selectors/views/selectedEnterprise/selectedEnterprise';
import { getHash } from '../../../../utils/getHash';
import { getConfirmation, getConfirmedReservations } from './typedSelectors';

export const getConfirmedReservationGroupId = createSelector(getConfirmation, get('reservationGroupId'));
export const getConfirmedTotalAmount = createSelector(getConfirmation, (confirmation) => confirmation?.totalAmount);
export const isReservationGroupFetching = flow(getConfirmation, get('isReservationGroupFetching'), Boolean);

export const getViewConfirmedReservations = createSelector(
    getConfirmedReservations,
    getSelectedEnterpriseCategories,
    getPromotedServiceCategories,
    (confirmedReservations, categories, promotedServiceCategories) =>
        pipe(
            confirmedReservations,
            array.map((r) => {
                const reservationCategoryName = categories[r.roomId]?.name;
                const promotedServiceCategoryName = promotedServiceCategories[r.roomId]?.name;
                const roomName = r.roomName ?? reservationCategoryName ?? promotedServiceCategoryName;

                return {
                    ...r,
                    roomName,
                };
            })
        )
);

const getConfirmedTotalAmountGross = createSelector(
    getSelectedEnterpriseDefaultRateCurrencyCode,
    getConfirmedTotalAmount,
    (defaultCurrencyCode, totalAmount) => get([defaultCurrencyCode, PricingModeKey.GrossValue], totalAmount)
);

const getTransactionId = (reservationGroupId: string, reservations: { id: string }[]) =>
    getHash(
        reservationGroupId +
            reservations
                .map(({ id }) => id)
                .sort()
                .join(',')
    );

export const getReservationGroupIntegrationData = createSelector(
    identity,
    getConfirmedReservationGroupId,
    getConfirmedTotalAmountGross,
    getViewConfirmedReservations,
    getSelectedEnterpriseDefaultRateCurrencyCode,
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseDefaultLanguageName,
    getClientName,
    (
        state,
        reservationGroupId,
        totalAmount,
        reservations,
        defaultCurrencyCode,
        defaultLanguageCode,
        hotelName,
        clientName
    ) => ({
        reservationGroupId,
        currencyCode: defaultCurrencyCode,
        totalCost: totalAmount,

        // google ecommerce transaction data
        ecommerce: {
            currencyCode: defaultCurrencyCode,

            // Enchanced Ecommerce - purchase
            purchase: {
                actionField: {
                    id: reservationGroupId,
                    affiliation: clientName,
                    revenue: totalAmount,
                },
                products: map(
                    ({ roomId, roomName, amount }) => ({
                        id: roomId,
                        name: localizeProperty(state, { languageCode: defaultLanguageCode, property: roomName }),
                        price: get([defaultCurrencyCode, PricingModeKey.GrossValue], amount),
                        category: hotelName,
                        quantity: 1,
                    }),
                    reservations
                ),
            },
        },

        // Basic ecommerce transaction
        transactionId: getTransactionId(String(reservationGroupId), reservations),
        transactionAffiliation: clientName,
        transactionTotal: totalAmount,
        transactionProducts: map(
            ({ confirmationNumber, roomName, amount }) => ({
                sku: confirmationNumber,
                name: localizeProperty(state, { languageCode: defaultLanguageCode, property: roomName }),
                price: get([defaultCurrencyCode, PricingModeKey.GrossValue], amount),
                category: hotelName,
                quantity: 1,
            }),
            reservations
        ),
    })
);
